import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type RowSelectionState,
  type OnChangeFn,
} from "@tanstack/react-table";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface DataTableProps<TData> {
  data: TData[];
  columns: ColumnDef<TData, any>[];
  isLoading?: boolean;
  isFetchingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onRowClick?: (row: TData) => void;
  emptyMessage?: string;
  rowKey: (row: TData) => string;
  footerNote?: React.ReactNode;
  /** Optional row-selection state (controlled). When provided, the table
   *  exposes selection via row.getIsSelected() etc. */
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  /** Query failed — takes priority over the empty-state message so a broken
   *  fetch never reads as "nothing here". */
  isError?: boolean;
  errorMessage?: string;
  /** Cheap retry hook (usually the query's own `refetch`). */
  onRetry?: () => void;
}

export function DataTable<TData>({
  data,
  columns,
  isLoading,
  isFetchingMore,
  hasMore,
  onLoadMore,
  onRowClick,
  emptyMessage = "No matching entries.",
  rowKey,
  footerNote,
  rowSelection,
  onRowSelectionChange,
  isError,
  errorMessage = "Failed to load — retry.",
  onRetry,
}: DataTableProps<TData>) {
  const table = useReactTable({
    getRowId: (row) => rowKey(row),
    state: rowSelection ? { rowSelection } : undefined,
    onRowSelectionChange,
    enableRowSelection: !!onRowSelectionChange,
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Card>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id} className="border-b text-left text-xs uppercase text-muted-foreground">
                {hg.headers.map((h) => (
                  <th key={h.id} className="px-4 py-2 font-medium">
                    {h.isPlaceholder ? null : flexRender(h.column.columnDef.header, h.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            ) : isError ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center">
                  <div className="flex flex-col items-center gap-2 text-destructive">
                    <span>{errorMessage}</span>
                    {onRetry ? (
                      <Button variant="outline" size="sm" onClick={onRetry}>
                        Retry
                      </Button>
                    ) : null}
                  </div>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center text-muted-foreground">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((r) => (
                <tr
                  key={rowKey(r.original)}
                  className={cn(
                    "border-b hover:bg-muted/50",
                    onRowClick ? "cursor-pointer" : undefined,
                  )}
                  onClick={onRowClick ? () => onRowClick(r.original) : undefined}
                >
                  {r.getVisibleCells().map((c) => (
                    <td key={c.id} className="px-4 py-2">
                      {flexRender(c.column.columnDef.cell, c.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between border-t p-3 text-xs">
        <div className="text-muted-foreground">
          {footerNote ?? `${data.length.toLocaleString()} rows${hasMore ? " (more available)" : ""}`}
        </div>
        {hasMore && onLoadMore ? (
          <Button variant="outline" size="sm" onClick={onLoadMore} disabled={isFetchingMore}>
            {isFetchingMore ? "Loading…" : "Load more"}
          </Button>
        ) : null}
      </div>
    </Card>
  );
}
